/**
 * What was dropped onto the Files table: files, and folders walked into their files.
 *
 * `dataTransfer.files` lists a dropped folder as one unreadable pseudo-file named like the folder;
 * uploading it fails in the browser ("Network Error"). Folders have to be read through
 * `DataTransferItem.webkitGetAsEntry()` (supported by Chrome, Edge, Firefox and Safari).
 */

import {MAX_FILES_PER_UPLOAD, TooManyFilesError} from './uploadLimits'

export interface DroppedFile {
  file: File
  /** Path inside the drop: `a.fq` for a dropped file, `run1/reads/a.fq` for one inside a folder. */
  relativePath: string
}

export interface DroppedContent {
  files: DroppedFile[]
  /** Folders (relative paths) that hold no files at all, so they need their own folder marker. */
  emptyFolders: string[]
}

/** Minimal shapes of the File and Directory Entries API, which TypeScript's DOM lib types loosely. */
export interface FsEntry {
  isFile: boolean
  isDirectory: boolean
  name: string
}
interface FsFileEntry extends FsEntry {
  file(success: (file: File) => void, failure?: (error: unknown) => void): void
}
interface FsDirectoryEntry extends FsEntry {
  createReader(): {readEntries(success: (entries: FsEntry[]) => void, failure?: (error: unknown) => void): void}
}

/** A drop as taken from its event: the entries to walk, and the plain file list as fallback. */
export interface DroppedEntries {
  entries: FsEntry[]
  files: File[]
}

/**
 * Takes the entries out of a drop event. Must be called while the event is being handled: the
 * browser empties `dataTransfer` once the handler returns. Reading the entries can happen later.
 */
export function droppedEntries(dataTransfer: DataTransfer): DroppedEntries {
  const entries = Array.from(dataTransfer.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() as FsEntry | null)
    .filter((entry): entry is FsEntry => Boolean(entry))
  return {entries, files: Array.from(dataTransfer.files ?? [])}
}

function readFile(entry: FsFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}

/** All children of a folder: readEntries returns them in batches (100 in Chrome) until empty. */
async function readChildren(entry: FsDirectoryEntry): Promise<FsEntry[]> {
  const reader = entry.createReader()
  const children: FsEntry[] = []
  for (;;) {
    const batch = await new Promise<FsEntry[]>((resolve, reject) => reader.readEntries(resolve, reject))
    if (batch.length === 0) return children
    children.push(...batch)
  }
}

async function walk(entry: FsEntry, path: string, out: DroppedContent, maxFiles: number): Promise<number> {
  if (entry.isFile) {
    // Stop as soon as the drop is too big, instead of first reading every entry of a huge folder.
    if (out.files.length >= maxFiles) throw new TooManyFilesError(out.files.length, false)
    const file = await readFile(entry as FsFileEntry)
    // Browsers always name an entry; Cypress's mocked drag-drop entry has no name, so a top-level
    // file falls back to the File's own name instead of uploading as "undefined".
    out.files.push({file, relativePath: path || file.name})
    return 1
  }
  if (!entry.isDirectory) return 0
  let fileCount = 0
  for (const child of await readChildren(entry as FsDirectoryEntry)) {
    fileCount += await walk(child, `${path}/${child.name}`, out, maxFiles)
  }
  if (fileCount === 0) out.emptyFolders.push(path)
  return fileCount
}

/**
 * The files of a drop, with folders expanded. A browser without entry support gets the plain file
 * list: files work there, folders can't be told apart (they fail to upload, as before). Throws
 * TooManyFilesError once the drop holds more than `maxFiles` files.
 */
export async function collectDroppedFiles(
  drop: DroppedEntries,
  maxFiles: number = MAX_FILES_PER_UPLOAD
): Promise<DroppedContent> {
  const out: DroppedContent = {files: [], emptyFolders: []}
  if (drop.entries.length === 0) {
    if (drop.files.length > maxFiles) throw new TooManyFilesError(drop.files.length, true)
    out.files = drop.files.map((file) => ({file, relativePath: file.name}))
    return out
  }
  for (const entry of drop.entries) {
    await walk(entry, entry.name ?? '', out, maxFiles)
  }
  return out
}
